/**
 * useWeeklyQuiz Hook
 *
 * Provides reactive access to the weekly quiz system including:
 * - This week's quiz questions
 * - Current attempt state
 * - Tech's quiz stats
 * - Completion status
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '@/firebase';
import { doc, onSnapshot, collection, query, where, limit } from 'firebase/firestore';
import { getTenantIdSync, tenantIdReadUnion } from '@/hooks/useTenantId';
import {
  WeeklyQuiz,
  QuizAttempt,
  TechQuizStats,
  QuizQuestion,
} from '@/types';
import {
  getThisWeeksQuiz,
  startQuizAttempt,
  submitQuizAnswer,
  completeQuizAttempt,
  getCurrentWeekId,
  saveQuizUIState,
  QUIZ_PAUSED,
} from '@/services/quiz.service';

interface UseWeeklyQuizReturn {
  // Quiz data
  quiz: WeeklyQuiz | null;
  loading: boolean;
  error: Error | null;

  // Attempt state
  attempt: QuizAttempt | null;
  hasCompletedThisWeek: boolean;

  // Current question tracking
  currentQuestionIndex: number;
  currentQuestion: QuizQuestion | null;

  // Actions
  startQuiz: () => Promise<void>;
  answerQuestion: (answer: 'a' | 'b' | 'c') => Promise<boolean>;
  nextQuestion: () => void;
  completeQuiz: () => Promise<QuizAttempt | null>;

  // Stats
  stats: TechQuizStats | null;
}

export function useWeeklyQuiz(techId: string | null, techName: string): UseWeeklyQuizReturn {
  const [quiz, setQuiz] = useState<WeeklyQuiz | null>(null);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [stats, setStats] = useState<TechQuizStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [hasCompletedThisWeek, setHasCompletedThisWeek] = useState(false);

  // Tracks whether the user is actively playing a quiz session.
  // When true, onSnapshot must NOT update currentQuestionIndex — only
  // nextQuestion() (called from the UI) should advance it.
  const sessionActiveRef = useRef(false);

  // Load this week's quiz
  useEffect(() => {
    const loadQuiz = async () => {
      try {
        setLoading(true);
        const weeklyQuiz = await getThisWeeksQuiz();
        setQuiz(weeklyQuiz);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load quiz'));
      } finally {
        setLoading(false);
      }
    };

    loadQuiz();
  }, []);

  // Listen for existing attempt for this week
  useEffect(() => {
    if (!techId) return;

    const weekId = getCurrentWeekId();

    const q = query(
      collection(db, 'quiz_attempts'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('techId', '==', techId),
      where('quizDate', '==', weekId),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const attemptData = {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data(),
        } as QuizAttempt;
        setAttempt(attemptData);
        setHasCompletedThisWeek(attemptData.status === 'completed');

        // Only update currentQuestionIndex from the snapshot when no active
        // session is in progress (i.e. on initial load / resume).  During
        // active play, nextQuestion() is the sole driver of index progression
        // so that onSnapshot doesn't skip questions or break the feedback screen.
        //
        // IMPORTANT: prefer the Firestore-stored currentQuestionIndex (which
        // represents "the question currently being viewed") over answers.length.
        // On the feedback screen, answers.length = i+1 while the user is still
        // viewing question i — using answers.length here would jump the index
        // forward and make FeedbackScreen render the NEXT question's explanation.
        // Falls back to answers.length for legacy attempts without the field.
        if (!sessionActiveRef.current) {
          const storedIndex =
            typeof attemptData.currentQuestionIndex === 'number'
              ? attemptData.currentQuestionIndex
              : attemptData.answers.length;
          setCurrentQuestionIndex(storedIndex);
        }
      } else {
        setAttempt(null);
        setHasCompletedThisWeek(false);
        setCurrentQuestionIndex(0);
        sessionActiveRef.current = false;
      }
    });

    return () => unsubscribe();
  }, [techId]);

  // Listen for tech's stats
  useEffect(() => {
    if (!techId) return;

    const statsRef = doc(db, 'quiz_stats', techId);

    const unsubscribe = onSnapshot(statsRef, (snapshot) => {
      if (snapshot.exists()) {
        setStats({ id: snapshot.id, ...snapshot.data() } as TechQuizStats);
      } else {
        setStats(null);
      }
    });

    return () => unsubscribe();
  }, [techId]);

  // Start a new quiz attempt
  const startQuiz = useCallback(async () => {
    if (!techId || !quiz) {
      throw new Error('Cannot start quiz: missing techId or quiz');
    }

    if (hasCompletedThisWeek) {
      throw new Error('Already completed this week\'s quiz');
    }

    if (attempt && attempt.status === 'in_progress') {
      // Resume existing attempt
      sessionActiveRef.current = true;
      setCurrentQuestionIndex(attempt.answers.length);
      return;
    }

    try {
      sessionActiveRef.current = true;
      const newAttempt = await startQuizAttempt(techId, techName, quiz);
      setAttempt(newAttempt);
      setCurrentQuestionIndex(0);
    } catch (err) {
      sessionActiveRef.current = false;
      setError(err instanceof Error ? err : new Error('Failed to start quiz'));
      throw err;
    }
  }, [techId, techName, quiz, hasCompletedThisWeek, attempt]);

  // Answer current question
  const answerQuestion = useCallback(
    async (answer: 'a' | 'b' | 'c'): Promise<boolean> => {
      if (!attempt || !quiz) {
        throw new Error('No active quiz attempt');
      }

      const question = quiz.questions[currentQuestionIndex];
      if (!question) {
        throw new Error('Invalid question index');
      }

      const isCorrect = answer === question.correctAnswer;

      try {
        await submitQuizAnswer(
          attempt.id!,
          question.id,
          answer,
          isCorrect,
          attempt.answers,
          currentQuestionIndex
        );

        // Update local state
        setAttempt((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            answers: [
              ...prev.answers,
              {
                questionId: question.id,
                selectedAnswer: answer,
                isCorrect,
                answeredAt: new Date().toISOString(),
              },
            ],
            currentQuestionIndex,
            currentScreen: 'feedback',
          };
        });

        return isCorrect;
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to submit answer'));
        throw err;
      }
    },
    [attempt, quiz, currentQuestionIndex]
  );

  // Move to next question
  const nextQuestion = useCallback(async () => {
    if (quiz && currentQuestionIndex < quiz.questions.length - 1 && attempt) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);

      // Save UI state to Firestore so user can resume
      try {
        await saveQuizUIState(attempt.id!, nextIndex, 'question');
        // Update local state
        setAttempt((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            currentQuestionIndex: nextIndex,
            currentScreen: 'question',
          };
        });
      } catch (err) {
        console.error('Failed to save quiz UI state:', err);
        // Don't throw - allow quiz to continue even if state save fails
      }
    }
  }, [quiz, currentQuestionIndex, attempt]);

  // Complete the quiz
  const completeQuiz = useCallback(async (): Promise<QuizAttempt | null> => {
    if (!attempt || !quiz || !techId) {
      return null;
    }

    try {
      const completedAttempt = await completeQuizAttempt(
        attempt.id!,
        attempt.answers,
        quiz.questions.length,
        quiz,
        techId,
        techName
      );

      setAttempt(completedAttempt);
      setHasCompletedThisWeek(true);
      sessionActiveRef.current = false;

      return completedAttempt;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to complete quiz'));
      throw err;
    }
  }, [attempt, quiz, techId, techName]);

  // Get current question
  const currentQuestion =
    quiz && currentQuestionIndex < quiz.questions.length
      ? quiz.questions[currentQuestionIndex]
      : null;

  return {
    quiz,
    loading,
    error,
    attempt,
    hasCompletedThisWeek,
    currentQuestionIndex,
    currentQuestion,
    startQuiz,
    answerQuestion,
    nextQuestion,
    completeQuiz,
    stats,
  };
}

/** @deprecated Use useWeeklyQuiz instead */
export const useDailyQuiz = useWeeklyQuiz;

/**
 * Hook for office dashboard - gets all tech quiz stats
 */
export function useAllQuizStats() {
  const [stats, setStats] = useState<TechQuizStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'quiz_stats'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allStats = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() } as TechQuizStats))
        .sort((a, b) => b.lifetimeAverageScore - a.lifetimeAverageScore);

      setStats(allStats);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { stats, loading };
}

/**
 * Hook to check if this week's quiz is available and not completed
 */
export function useQuizNotification(techId: string | null) {
  const [isQuizAvailable, setIsQuizAvailable] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!techId) {
      setLoading(false);
      return;
    }

    // Quiz is paused — don't pulse the bell or hit Firestore. Tapping
    // the bell still opens the modal, which shows the pause message.
    if (QUIZ_PAUSED) {
      setIsQuizAvailable(false);
      setHasCompleted(false);
      setLoading(false);
      return;
    }

    const checkQuizStatus = async () => {
      try {
        // Check if quiz exists for this week
        const quiz = await getThisWeeksQuiz();
        setIsQuizAvailable(!!quiz && quiz.active);

        // Check if completed
        const weekId = getCurrentWeekId();
        const q = query(
          collection(db, 'quiz_attempts'),
          where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
          where('techId', '==', techId),
          where('quizDate', '==', weekId),
          where('status', '==', 'completed'),
          limit(1)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          setHasCompleted(!snapshot.empty);
          setLoading(false);
        });

        return () => unsubscribe();
      } catch {
        setLoading(false);
      }
    };

    checkQuizStatus();
  }, [techId]);

  return {
    showNotification: isQuizAvailable && !hasCompleted && !loading,
    hasCompleted,
    loading,
  };
}
